import React, { useEffect, useMemo, useState } from 'react';
import { CANDIDATE_KEY_DOCUMENTS } from '../../../../constants/app';
import useFileNameLength from '../../../../hooks/useFileNameLength';
import { OTHER_QUALIFICATIONS_LEVEL } from '../../../../models/Onboarding/onboarding-values';
import { DOCUMENT_CONFIGS } from '../../../../models/superadmin';
import { useAppDispatch as useDispatch } from '../../../../store';
import { ICandidateDetails } from '../../../../store/superadmin/types';
import { updateDocumentDateThunk } from '../../../../store/uploads/thunks';
import { getCountryName } from '../../../../utils/documentHelper';
import Warning from '../../../UI/Icons/Warning';
import KeyDocumentsSection from './KeyDocumentsSection';

interface QualificationProps {
  showQualifications: boolean;
  reviewCandidateData: ICandidateDetails | null;
  convertToTitleCase: (str: string) => string;
  handleFileSelect: (file: any, type: string) => void;
}

const Qualification: React.FC<QualificationProps> = ({
  showQualifications, 
  reviewCandidateData, 
  convertToTitleCase, 
  handleFileSelect
}) => {
  const dispatch = useDispatch();

  const [documentDates, setDocumentDates] = useState<Record<string, Date | null>>({});

  const candidateVerificationStatus = reviewCandidateData?.profile.verification_status;
  const qualificationAcceptOptions = reviewCandidateData?.qualifications?.qualification_accept_options ? JSON.parse(reviewCandidateData?.qualifications?.qualification_accept_options) : [];
  const alternativePositions = reviewCandidateData?.qualifications?.alternative_positions ? JSON.parse(reviewCandidateData?.qualifications?.alternative_positions) : [];
  const educatorTeacherPositions = (() => {
    const val = reviewCandidateData?.qualifications?.educator_teacher_positions;
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  })();
  const candidateDocumentList = useMemo(() => reviewCandidateData?.documents || [], [reviewCandidateData?.documents]);
  const expiringDocumentsCollections = reviewCandidateData?.documents_expire_one_month_documents;
  const { maxLength, preserveEndChars } = useFileNameLength();

  const matchedMultipleTitles: string[] = [];
  const expiredTitles: string[] = [];
  
  expiringDocumentsCollections?.forEach((doc) => {
    const match = DOCUMENT_CONFIGS.find((config) => config.type === doc.document_type);

    if (!match) return;

    const title = match.title;
    const isExpired = doc.is_expired === 1;

    if (isExpired) {
      expiredTitles.push(title);
    } else {
      matchedMultipleTitles.push(match.title);
    }
  });

  const formattedExpired = expiredTitles.join(", ");
  const formattedMultipleTitles = matchedMultipleTitles.join(", ");

  useEffect(() => {
    const getDocumentByTypeLocal = (type: string) =>
      candidateDocumentList?.find((file: any) => file.document_type === type);

    const getDocumentDateByType = (type: string): Date | null => {
      if (!CANDIDATE_KEY_DOCUMENTS.includes(type)) return null;

      const document = getDocumentByTypeLocal(type);
      if (!document) return null;

      // Determine which date field to use based on document type
      if (type === "working_with_children_check" || type === "teachers_registration" || type === "australian_work_visa") {
        return document.validity_date ? new Date(document.validity_date) : null;
      } else if (type === "first_aid_certificate" || type === "mandatory_reporting_training" || type === "food_handler_certificate") {
        return document.issued_date ? new Date(document.issued_date) : null;
      } else if (type === "diploma" || type === "post_graduate_diploma" || type === "bachelor_or_degree" || type === "certificate_iii" || type === "cooking_qualification") {
        return document.completion_date ? new Date(document.completion_date) : null;
      }

      return null;
    };

    const newDates: Record<string, Date | null> = {};
    DOCUMENT_CONFIGS.forEach((doc) => {
      const type = doc.type;
      const val = getDocumentDateByType(type);
      newDates[type] = val;
    });

    setDocumentDates(newDates);
  }, [candidateDocumentList]);

  const handleDocDate = async (type: string, val: Date | null) => {
    if (!val) return;

    setDocumentDates((prev) => ({
      ...prev,
      [type]: val,
    }));

    const year = val.getFullYear();
    const month = String(val.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(val.getDate()).padStart(2, '0');

    const dateOnly = `${year}-${month}-${day}`; // Local date in YYYY-MM-DD
    dispatch(updateDocumentDateThunk({ document_type: type, date: dateOnly, candidate_uuid: reviewCandidateData?.uuid, isAdmin: true }));
  };


      
  return (
    <div
      className={`d-flex flex-column gap-20 fw-400 text-black ${
        showQualifications ? "" : "d-none"
      }`}
    >
        <div className="fs-22">Qualifications</div>
        { (candidateVerificationStatus === 'verified' && (formattedExpired.length > 0 || formattedMultipleTitles.length > 0)) ? 
        <div className="d-flex w-100 max-w-484px">
          <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
            <Warning />
            <div className="fs-13 text-black fw-400">
              This employee’s{" "}
              {formattedExpired.length > 0 && formattedMultipleTitles.length > 0 ? (
                <>
                  <span className="fw-700">{formattedExpired}</span> has expired and{" "}
                  <span className="fw-700">{formattedMultipleTitles}</span> expires soon.
                </>
              ) : formattedExpired.length > 0 ? (
                <>
                  <span className="fw-700">{formattedExpired}</span> has expired.
                </>
              ) : formattedMultipleTitles.length > 0 ? (
                <>
                  <span className="fw-700">{formattedMultipleTitles}</span> expires soon.
                </>
              ) : (
                ""
              )}{" "}
              They are required to upload a valid document in order to continue using
              Connected Crew.
            </div>
          </div>
        </div> : "" }
        <div className="d-flex flex-column gap-5">
          <div className="fs-13 text-grey-medium">
            Which qualification do you have in Early Childhood Education?
          </div>
          <div className="fs-16">
            { reviewCandidateData?.qualifications?.qualification ===
            "certificate_iii"
              ? OTHER_QUALIFICATIONS_LEVEL.CERTIFICATE_III
              : reviewCandidateData?.qualifications?.qualification ===
                "currently_studying" && reviewCandidateData?.qualifications?.currently_studying_option === "diploma_equivalent" 
              ? "I am currently studying and meet the equivalent requirements of a Diploma level" 
              : reviewCandidateData?.qualifications?.qualification ===
                "currently_studying" && reviewCandidateData?.qualifications?.currently_studying_option === "certificate_iii_equivalent" 
              ? "I am currently studying and meet the equivalent requirements of a Certificate III level"
              : convertToTitleCase(
                  reviewCandidateData?.qualifications?.qualification ??
                    "No data"
                )}
          </div>
        </div>
        {(reviewCandidateData?.qualifications?.qualification === "bachelor_or_degree" || reviewCandidateData?.qualifications?.qualification === "post_graduate_diploma") && qualificationAcceptOptions.length > 0 && (
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">
             What other qualification levels in Early Childhood Education would you be open to accepting?
            </div>
            <div className="fs-16">
              {qualificationAcceptOptions.length > 0
                ? qualificationAcceptOptions.join(", ")
                : ""}
            </div>
          </div>
        )}

        <div className="d-flex flex-column gap-5">
          <div className="fs-13 text-grey-medium">
            Where did you complete your studies in Early Childhood Education?
          </div>
          <div className="fs-16">
            {reviewCandidateData?.qualifications
              ?.studies_completed_location === "other"
              ? getCountryName(convertToTitleCase(
                  reviewCandidateData?.qualifications
                    ?.other_study_selected_country ?? "No data"
                ))
              : convertToTitleCase(
                  reviewCandidateData?.qualifications
                    ?.studies_completed_location ?? "No data"
                )} 
          </div>
        </div>
        {reviewCandidateData?.qualifications?.studies_completed_location ===
          "new_zealand" && (
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">is ACECQA approved</div>
            <div className="fs-16">
              {reviewCandidateData?.qualifications?.acecqa_approved === 1
                ? "Yes"
                : "No"}
            </div>
          </div>
        )}

        {reviewCandidateData?.qualifications?.qualification === "diploma" && (
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">
              Would you be open to accepting jobs at a Certificate III Educator level?
            </div>
            <div className="fs-16">
              {reviewCandidateData?.qualifications
                ?.diploma_jobs_at_certificate_level === "Yes"
                ? "Yes"
                : "No"}
            </div>
          </div>
        )}

        {/** 
         * Render the Alternative Positions
         */}
        {alternativePositions.length > 0 || educatorTeacherPositions.length > 0 ? (
          <>
            <div className="bg-off-white min-h-1px"></div>
            <div className="fs-22">Position Types</div>
            {
              reviewCandidateData?.qualifications?.qualification !== "no_qualifications" && (
                <div className="d-flex flex-column gap-5">
                  <div className='text-grey-medium fs-13'>Educator/Teacher Positions:</div>
                  <div className="fs-16">
                    {educatorTeacherPositions.length > 0
                      ? educatorTeacherPositions
                          .filter(
                            (item: string) => !["1", "2", "5"].includes(item)
                          )
                          .join(", ") || "No data"
                      : "No data"}
                  </div>
                </div>
              )
            }
            <div className="d-flex flex-column  gap-5">
              <div className='text-grey-medium fs-13'>Alternative Positions:</div>
              <div className="fs-16">
                {alternativePositions.length > 0
                  ? alternativePositions
                      .filter(
                        (item: string) => !["1", "2", "5"].includes(item)
                      )
                      .join(", ") || "No data"
                  : "No data"}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-off-white min-h-1px"></div>
            <div className="fs-22">Position Types</div>
            <div className='fs-16'>No Data</div>
          </>
        )}

        {/** 
         * Render the Qualification Documents
         */}
        <KeyDocumentsSection
          reviewCandidateData={reviewCandidateData}
          candidateVerificationStatus={candidateVerificationStatus}
          candidateDocumentList={candidateDocumentList}
          expiringDocumentsCollections={expiringDocumentsCollections ?? []}
          documentDates={documentDates}
          handleFileSelect={handleFileSelect}
          handleDocDate={handleDocDate}
          alternativePositions={alternativePositions}
        />
    </div>
  );
};

export default Qualification;